/**
 * Undo handlers for file operations — ID-based
 * Import this module to register the handlers with the undo system.
 */
import { registerUndoHandler, undoUnconfirmedError, type UndoOperation, type UndoHandlerContext } from '@papan/core-ui/ui'
import { getActiveTenantId } from '@papan/api-client'
import { isMountFileId, MOVE_JOB_STATUS_UNKNOWN_CODE } from '@papan/types'
import { filesApi } from './index.js'
import { createRestoreTracker } from './restore-tracker.js'
import { createDeleteTracker, type DeleteJobOutcome } from './delete-tracker.js'
import { JobLostError } from './job-tracker.js'

/**
 * E7 PR 0b (D134) — an undo, once started, RUNS TO COMPLETION in the session that started it: every request it makes
 * acts as that session's identity (`pinned(ctx)` — the undo provider pins it at the start), so a tenant / identity switch
 * mid-undo can't send its follow-on steps as the new context, and nothing stops it between its steps (a half-done undo
 * was worse: both files in the Bin, copies never deleted). Every step is IDEMPOTENT, so a retry after a partial run
 * finishes it: the op records its progress, a delete of an item already gone counts as done, a restore whose Bin item
 * is gone but whose file is back counts as done, and a version restore refused "changed since" while the file's
 * current content already IS the restored version counts as done. (The provider drops only what it SHOWS once the
 * session ended, and clears its stack then.)
 */
function pinned(ctx?: UndoHandlerContext): { identity: NonNullable<UndoHandlerContext['identity']> } | undefined {
  if (!ctx?.identity) return undefined
  const { orgEmail, actingOrgEmail, shareSession } = ctx.identity
  // An identity that names NO org email lets the server use its stored choice — which another tab (or, after an account
  // change, another person) may have changed: refuse the step rather than act as someone else. Named explicitly (the
  // tab's own choice, or the email the server said it acted as) it is sent as that; another account's email is refused
  // by the server itself. A share session carries its own identity.
  if (!orgEmail && !shareSession) {
    if (!actingOrgEmail) {
      throw Object.assign(new Error('Cannot finish the undo: switch back to the identity you did this as'), { code: 'UNDO_IDENTITY_UNKNOWN' })
    }
    if (ctx.sameAccount && !ctx.sameAccount()) {
      throw Object.assign(new Error('Cannot finish the undo: the account changed'), { code: 'UNDO_ACCOUNT_CHANGED' })
    }
  }
  return { identity: ctx.identity }
}
const statusOf = (err: unknown): number | undefined => (err as { status?: number } | null)?.status

/** Delete; an item already gone (a retry after a partial run) is done — but counted as NOT CONFIRMED (a 404 is also
 *  what an item this identity can no longer see answers), so the toast says some items were no longer reachable. A
 *  share-mount (compound) id answers a missing item 403 like a refused one: only a read answering 404 makes it
 *  "gone" — still there, refused, or any other failure keeps the original 403. */
async function deleteIfThere(fileId: string, ctx?: UndoHandlerContext): Promise<void> {
  try {
    await filesApi.deleteFile(fileId, pinned(ctx))
  } catch (err) {
    const status = statusOf(err)
    if (status === 404) { if (ctx?.outcome) ctx.outcome.unreachable++; return }
    if (status === 403 && isMountFileId(fileId)) {
      // only an actual 404 on the read means gone; a refusal / blip / still-there keeps the original 403
      const gone = await filesApi.fileGone(fileId, pinned(ctx)).catch(() => false)
      if (gone) {
        if (ctx?.outcome) ctx.outcome.unreachable++
        return
      }
    }
    throw err
  }
}

/** "Changed since" (409 VERSION_CHANGED) on a version restore — but is each file's CURRENT content already the version
 *  it was to be restored to (an earlier attempt of this undo landed and its answer was lost)? Then it is done. */
async function alreadyRestored(items: Array<{ fileId: string; previousVersionId: string }>, ctx?: UndoHandlerContext): Promise<boolean> {
  for (const it of items) {
    const { versions } = await filesApi.getVersions(it.fileId, pinned(ctx))
    const target = versions.find((v) => v.id === it.previousVersionId)
    const current = [...versions].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0) || b.createdAt.localeCompare(a.createdAt))[0]
    if (!target || !current || current.contentHash !== target.contentHash) return false
  }
  return true
}
const isVersionChanged = (err: unknown) => statusOf(err) === 409 && (err as { code?: string } | null)?.code === 'VERSION_CHANGED'

/**
 * Restore a Bin entry and resolve only once it is BACK — the restore an undo needs done (#921).
 *
 * A big folder's restore takes minutes inline (20,000 files: 175 s on stagetest), longer than
 * Cloudflare waits for an origin answer (125 s): the undo then failed at the edge while the restore
 * went on. So it asks for `Prefer: respond-async` like the Bin's own Restore — a small entry still
 * answers inline, a big one 202 + a `file-restore` job — and follows the job through its status
 * route only (this runs outside React, with no socket of its own; the route is the job tracker's
 * safety net, asked every 2 s). Refusals throw exactly as `restoreFromBin`'s do; a job that fails
 * throws `JobFailedError` with the server's message. D134: the request AND the status reads act as the
 * pinned identity (`pinned(ctx)`).
 */
export async function restoreUntilBack(binId: string, opts: { rebuildPath: true }, ctx?: UndoHandlerContext): Promise<void> {
  const operationId = `undo-restore-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const tracker = createRestoreTracker(
    { onOperationEvent: () => () => {}, isConnected: () => false, getStatus: (jobId) => filesApi.getRestoreStatus(jobId, pinned(ctx)), runsToCompletion: !!ctx?.identity },
    operationId,
    { tickMs: 1_000, offlinePollMs: 2_000 },
  )
  try {
    const started = await filesApi.restoreFromBinAsync(binId, operationId, undefined, { ...opts, ...pinned(ctx) })
    // (a 404 in the first seconds — the 202 beat the job into BullMQ — is the tracker's own "not visible yet")
    if (started.kind === 'queued') await tracker.follow(started.jobId, started.operationId)
  } finally {
    tracker.dispose()
  }
}

/**
 * Delete an item and resolve only once it is GONE — the delete an undo needs done (#921), as
 * `restoreUntilBack` is for a restore. A small item still answers inline; a big folder (≥ 2,000 nodes,
 * the server decides) runs as a `file-delete` job, followed through its status route only (no socket
 * here). Answers the delete's outcome in `deleteFile`'s shape — a split delete carries `skipped`.
 * Refusals throw as `deleteFile`'s do; a lost job throws `JobLostError`, a failed one `JobFailedError`.
 */
export async function deleteUntilGone(fileId: string, ctx?: UndoHandlerContext): Promise<DeleteJobOutcome> {
  const operationId = `undo-delete-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const tracker = createDeleteTracker(
    { onOperationEvent: () => () => {}, isConnected: () => false, getStatus: (jobId) => filesApi.getDeleteStatus(jobId, pinned(ctx)), runsToCompletion: !!ctx?.identity },
    operationId,
    { tickMs: 1_000, offlinePollMs: 2_000 },
  )
  try {
    const started = await filesApi.deleteFileAsync(fileId, operationId, pinned(ctx))
    if (started.kind === 'queued') return await tracker.follow(started.jobId, started.operationId)
    const { kind: _kind, ...done } = started
    return done
  } finally {
    tracker.dispose()
  }
}

/**
 * An undo's delete of what it made (a paste, an extract, a created folder, a cross-org move's landed copy): through
 * its job when big (#921). Its outcome, judged by D134's rules and #921's together:
 *  - a job whose outcome was lost (it may have finished) is "couldn't confirm" (`undoUnconfirmedError`), and the id is
 *    recorded (`deleteUnconfirmedIds`) for the retry;
 *  - a 404 in the person's OWN drive is already gone: done, counted as not confirmed (`outcome.unreachable`);
 *  - a 404 through a mount (`s_…`, a received `.shared` / a `.chain`) is NOT proof: there it also answers a revoked
 *    share or chain while the item still exists (codex review) — after a lost job "couldn't confirm", else the 404;
 *  - a 403 through a mount is gone only when a read answers 404 (`deleteIfThere`'s rule).
 * Answers the delete's outcome, or `undefined` when the item was already gone. Recording the id as done
 * (`deletedIds`) is the caller's, so it can judge the outcome first.
 */
async function deleteMade(
  fileId: string,
  ctx: UndoHandlerContext | undefined,
  op: { deleteUnconfirmedIds?: string[] },
  label: string,
): Promise<DeleteJobOutcome | undefined> {
  try {
    return await deleteUntilGone(fileId, ctx)
  } catch (err) {
    if (err instanceof JobLostError) {
      if (!op.deleteUnconfirmedIds?.includes(fileId)) op.deleteUnconfirmedIds = [...(op.deleteUnconfirmedIds ?? []), fileId]
      throw undoUnconfirmedError(label)
    }
    const status = statusOf(err)
    if (status === 404 && isMountFileId(fileId)) {
      if (op.deleteUnconfirmedIds?.includes(fileId)) throw undoUnconfirmedError(label)
      throw err
    }
    const gone = status === 404
      || (status === 403 && isMountFileId(fileId) && (await filesApi.fileGone(fileId, pinned(ctx)).catch(() => false)))
    if (!gone) throw err
    if (ctx?.outcome) ctx.outcome.unreachable++
    return undefined
  }
}

/** `deleteMade` for an item an undo deletes in a list: skipped once done; recorded in `deletedIds` (a retry skips it,
 *  and never counts it unreachable twice). */
async function deleteCreated(fileId: string, ctx: UndoHandlerContext | undefined, op: { deletedIds?: string[]; deleteUnconfirmedIds?: string[] }, label: string): Promise<void> {
  if (op.deletedIds?.includes(fileId)) return
  await deleteMade(fileId, ctx, op, label)
  op.deletedIds = [...(op.deletedIds ?? []), fileId]
}

/** Is this Bin entry GONE (a 404)? A check that fails is not an answer: not gone. */
async function binEntryGone(binId: string, ctx?: UndoHandlerContext): Promise<boolean> {
  try {
    return !(await filesApi.binItemExists(binId, pinned(ctx)))
  } catch {
    return false
  }
}

/**
 * A restore an undo needs DONE (`restoreUntilBack`), judged by D134's rules: a 404 (the Bin entry gone) whose item is
 * back in the drive is done (an earlier attempt landed, or someone restored it). A restore job whose outcome is unknown
 * (#921 — gone after its hour, never queued, or past the deadline): the drive is the truth — done only when the Bin
 * entry is gone AND the item is back (the entry goes when the restore commits; a restore still running is not done);
 * otherwise "couldn't confirm — check the Bin" (localized from the code), never the generic "failed".
 */
async function restoreConfirmed(binId: string, fileId: string | undefined, name: string, ctx?: UndoHandlerContext): Promise<void> {
  try {
    await restoreUntilBack(binId, { rebuildPath: true }, ctx)
  } catch (err) {
    if (statusOf(err) === 404 && fileId && (await filesApi.fileExists(fileId, pinned(ctx)))) return
    if (err instanceof JobLostError) {
      if ((await binEntryGone(binId, ctx)) && fileId && (await filesApi.fileExists(fileId, pinned(ctx)))) return
      throw undoUnconfirmedError(name)
    }
    throw err
  }
}

/**
 * Reverse a delete by restoring from bin. On 404 (bin entry gone),
 * disambiguate two shapes:
 *   - manual restore already happened → file id still present in
 *     drive → silently consume the undo (desired end-state holds).
 *   - bin entry was purged or auto-expired → file id absent → re-throw
 *     so UndoProvider surfaces the error.
 *
 * Exported separately so the registry side-effect at module load is
 * still the canonical wiring, but the logic stays unit-testable with
 * a mocked filesApi.
 */
export async function handleDeleteUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'delete') return
  // Undo puts back exactly what this person just deleted: if its folder went too, rebuild it where it was (#826) rather
  // than ask — through the restore job when big (#921); a 404 with the file back is a no-op success
  await restoreConfirmed(op.binId, op.fileId, op.name, ctx)
}
registerUndoHandler('delete', handleDeleteUndo)

/**
 * #801 — undo a SPLIT delete: restore every bin item the split made, each with
 * the SAME purge detection a whole delete has (404 + the file back ⇒ no-op;
 * 404 + gone ⇒ throw, so the provider keeps the op and says the undo failed).
 */
export async function handleDeleteSplitUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'deleteSplit') return
  for (let i = 0; i < op.binIds.length; i++) {
    await handleDeleteUndo({ id: op.id, timestamp: op.timestamp, type: 'delete', binId: op.binIds[i], fileId: op.fileIds[i], name: op.name }, ctx)
  }
}
registerUndoHandler('deleteSplit', handleDeleteSplitUndo)

type MoveBack = Awaited<ReturnType<typeof filesApi.moveFile>>

/** The drive root has two spellings (`null` in the client, `papan_<tenant>` from the server): one for comparing. */
const sameParent = (a: string | null | undefined, b: string | null | undefined) => {
  const root = `papan_${getActiveTenantId()}`
  const norm = (p: string | null | undefined) => (p == null || p === root ? null : p)
  return norm(a) === norm(b)
}

/** Is the item under `parentId` now? A lookup that fails is not an answer: no. */
async function isUnder(fileId: string, parentId: string | null, ctx?: UndoHandlerContext): Promise<boolean> {
  try {
    return sameParent((await filesApi.getFileInfo(fileId, pinned(ctx))).parentId, parentId)
  } catch {
    return false
  }
}

/**
 * One move-back of an undo, judged on its OWN answer — never assumed to have worked:
 *  - a 409 name conflict (moveFile THROWS it, `conflict: true`) → a same-named item is in the way;
 *  - a returned gesture (access confirmation, chain escape, cross-tenant) → nothing moved;
 *  - a SPLIT (#801: the item gained something the person can't move since) → part of it
 *    stayed where it is, so the undo is not done;
 *  - a big folder's move-back job whose outcome was lost (`MOVE_JOB_STATUS_UNKNOWN`, #1167 S4) → it may
 *    have run: "couldn't confirm", and the id is recorded on the op (`moveUnconfirmedIds`) so the retry
 *    first looks — already back under `parentId` is done (as a lost delete's 404 is);
 *    otherwise the retry moves it again (a lost job still queued then lands as a same-parent no-op).
 * Anything else thrown propagates unchanged.
 */
async function moveBack(
  fileId: string,
  update: Parameters<typeof filesApi.moveFile>[1] & { parentId: string | null },
  op: { name: string; moveUnconfirmedIds?: string[] },
  ctx?: UndoHandlerContext,
): Promise<void> {
  if (op.moveUnconfirmedIds?.includes(fileId) && (await isUnder(fileId, update.parentId, ctx))) {
    op.moveUnconfirmedIds = op.moveUnconfirmedIds.filter((id) => id !== fileId)
    return
  }
  let r: MoveBack
  try {
    r = await filesApi.moveFile(fileId, update, pinned(ctx))
  } catch (err) {
    if ((err as { conflict?: unknown })?.conflict === true) {
      throw new Error('Cannot undo: a same-named item now exists in the original folder')
    }
    if ((err as { code?: unknown } | null)?.code === MOVE_JOB_STATUS_UNKNOWN_CODE) {
      if (!op.moveUnconfirmedIds?.includes(fileId)) op.moveUnconfirmedIds = [...(op.moveUnconfirmedIds ?? []), fileId]
      throw undoUnconfirmedError(op.name, 'drive') // a move back: look in the drive, not the Bin
    }
    throw err
  }
  if (op.moveUnconfirmedIds?.includes(fileId)) op.moveUnconfirmedIds = op.moveUnconfirmedIds.filter((id) => id !== fileId)
  if (r.accessChange || r.needsConfirmation) throw new Error('Cannot undo: the move back needs an access confirmation')
  if (r.chainEscape || r.crossTenant) throw new Error('Cannot undo: the move back is no longer a plain move')
  if (r.split) throw new Error('Cannot undo: part of the folder could not be moved back')
}

/**
 * #801 — undo a SPLIT move: the root never moved, so each moved branch goes
 * back UNDER THE ROOT. `confirmedAccessChange` only restores what the split
 * move itself changed; anything the move-back still stops on (a same-named
 * item now in the root, a confirmation, a chain escape, a cross-tenant
 * gesture, a move-back that itself split) is refused rather than silently
 * confirmed or reported as undone. Then the shell the split created is
 * removed — only when it is provably empty (a truncated count is a lower
 * bound, not proof).
 */
export async function handleMoveSplitUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'moveSplit') return
  for (const id of op.moved) await moveBack(id, { parentId: op.rootId, confirmedAccessChange: true }, op, ctx)
  // L4 round 6: the shell is DISCARDED by the server — no Bin entry — which checks it is a
  // split shell (the marker the move stamped) and empty of everything, items the caller can't
  // see included. Refused (not empty / no write) ⇒ the folder is kept.
  const r = await filesApi.discardSplitShell(op.destFolderId, pinned(ctx))
  if (r.discarded) return
  // Kept as it is: not empty, someone else's, or not ours to judge (403/404).
  if (r.reason !== 'not_shell' && r.reason !== 'expired' && r.reason !== 'has_shares') return
  // Not a no-Bin case — a shell made before the marker existed, one past the undo window, or
  // one anchoring a share link / org share: today's rule, binned only when provably empty.
  // Empty = no descendants: `fileCount + folderCount` (what the route counts below the
  // folder). Not `count`, which INCLUDES the folder itself (L4 DEF-1: `count === 0` kept
  // every shell).
  const { fileCount, folderCount, truncated } = await filesApi.countChildren(op.destFolderId, pinned(ctx))
  // Inline, not the delete job: only a provably EMPTY folder is deleted here, never a big tree (#921).
  if (fileCount + folderCount === 0 && !truncated) await deleteIfThere(op.destFolderId, ctx)
}
registerUndoHandler('moveSplit', handleMoveSplitUndo)

/** Undo a WHOLE move: move the item back to its original parent, judged like a split's. */
export async function handleMoveUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'move') return
  await moveBack(op.fileId, { parentId: op.originalParentId }, op, ctx)
}
registerUndoHandler('move', handleMoveUndo)

/**
 * Undo a move ACROSS organisations: the source back from its owning tenant's Bin FIRST (if it is
 * gone for good the undo fails with nothing touched), then the landed item taken out. Both through
 * their jobs (#921): a big folder takes minutes either way, past Cloudflare's 125 s.
 *
 * A retry after a partial run (D134) finishes it, never redoes it:
 *  - the take-out runs ONLY once the source is CONFIRMED back (`sourceRestored`: recorded, or its Bin item gone and
 *    the item live again). A restore that 404s with the item not back, or whose job was lost, stops the undo with
 *    nothing taken out — a Bin entry being gone is NOT proof of a restore (a purged one is gone too), and deleting the
 *    landed copy then would destroy the only copy;
 *  - the landed item's delete is judged by `deleteMade` (already gone in the own drive: done, not confirmed; a lost job:
 *    "couldn't confirm"; a 404 through a mount is never proof);
 *  - a SPLIT take-out (something in the landed copy the person can't remove) is not undone.
 */
export async function handleMoveAcrossOrgsUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'moveAcrossOrgs') return
  if (!op.sourceRestored) {
    await restoreConfirmed(op.binId, op.sourceFileId, op.name, ctx)
    op.sourceRestored = true
  }
  if (op.deletedIds?.includes(op.newFileRef)) return
  const outcome = await deleteMade(op.newFileRef, ctx, op, op.name)
  // A split delete left part of the landed copy (something in it the person can't remove): not undone.
  if (outcome?.skipped) throw new Error(`Part of "${op.name}" could not be taken back out`)
  op.deletedIds = [op.newFileRef]
}
registerUndoHandler('moveAcrossOrgs', handleMoveAcrossOrgsUndo)

// Register rename undo handler - renames back to original name
registerUndoHandler('rename', async (op: UndoOperation, ctx?: UndoHandlerContext) => {
  if (op.type !== 'rename') return
  await filesApi.moveFile(op.fileId, { name: op.originalName }, pinned(ctx))
})

/** Restore the version each Replace overwrote (a Replace never creates the file, so undo never deletes it)
 *  — only while the version that Replace wrote is still current (expected-current check): if someone
 *  wrote the file since (another Replace, an edit) the server refuses "changed since — open Version
 *  History", and nothing of theirs is overwritten. D134: progress is recorded on the op as each step lands, and a
 *  "changed since" whose file already holds the restored content (an earlier attempt landed) counts as done. */
async function restoreReplacedVersions(
  op: { versioned?: Array<{ fileId: string; previousVersionId: string; versionId?: string }>; checkedRestored?: boolean; legacyRestored?: string[] },
  ctx?: UndoHandlerContext,
): Promise<void> {
  const all = op.versioned ?? []
  // ONE server operation for every file that carries the version its Replace wrote (all or nothing:
  // one file changed since → nothing restored). An entry recorded before that existed restores alone.
  const checked = all.flatMap((v) => (v.versionId ? [{ fileId: v.fileId, previousVersionId: v.previousVersionId, expectCurrentVersionId: v.versionId }] : []))
  if (checked.length > 0 && !op.checkedRestored) {
    try {
      await filesApi.undoReplace(checked, undefined, pinned(ctx))
    } catch (err) {
      if (!(isVersionChanged(err) && (await alreadyRestored(checked, ctx)))) throw err
    }
    op.checkedRestored = true
  }
  for (const v of all) {
    if (v.versionId || op.legacyRestored?.includes(v.fileId)) continue
    await filesApi.restoreVersion(v.fileId, v.previousVersionId, pinned(ctx))
    ;(op.legacyRestored ??= []).push(v.fileId)
  }
}

/** Undo a paste: delete what it created AND restore what its Replace overwrote — one step. */
export async function handleCopyUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'copy') return
  // Versions FIRST: if one can't be restored the undo fails with nothing deleted yet (it stays undoable).
  if (!op.versionsRestored) await restoreReplacedVersions(op, ctx)
  // Done — a retry (a delete below failing) must not ask for them again. Flagged, never emptied: the
  // undo's label still counts what it replaced ("Undone: replaced 1 file").
  op.versionsRestored = true
  // Each delete through its job when big (#921: a pasted 30,000-file folder takes minutes, past Cloudflare's 125 s).
  // `deletedIds` records each one done — never removed from `copiedFileIds`: the undo's label counts it.
  for (const fileId of op.copiedFileIds) await deleteCreated(fileId, ctx, op, 'paste')
}
registerUndoHandler('copy', handleCopyUndo)

/**
 * Undo a folder creation: delete the folder — through its job when big (#921: what was put in it since can be a big
 * tree — a paste or an upload into it — and an inline delete of that is cut at the edge while the server goes on).
 */
export async function handleCreateFolderUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'createFolder') return
  await deleteCreated(op.folderId, ctx, op, op.name)
}
registerUndoHandler('createFolder', handleCreateFolderUndo)

/**
 * Undo a Replace (a new version written into an existing file): restore the version it replaced. A
 * MOVE with Replace also sent the moved file to the Bin — the SAME step brings it back (moved file
 * first: if it is gone for good the undo fails before touching the destination).
 */
export async function handleAddVersionUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'addVersion') return
  if (op.versionId) {
    // ONE server operation: the destination's version (only while the Replace's version is current)
    // AND the moved file back from the Bin — all or nothing.
    const target = [{ fileId: op.fileId, previousVersionId: op.previousVersionId }]
    try {
      await filesApi.undoReplace([{ fileId: op.fileId, previousVersionId: op.previousVersionId, expectCurrentVersionId: op.versionId }], op.restoreSource?.binId, pinned(ctx))
    } catch (err) {
      if (op.restoreSource) {
        // The server checks the moved file's Bin row FIRST and is all or nothing: a "changed since" means an earlier
        // attempt did NOT land (never "done" — the moved file is still in the Bin). An earlier attempt that landed
        // (its answer lost) answers 404 (the Bin row gone): done only when the moved file is back AND the version is.
        if (statusOf(err) === 404 && (await filesApi.fileExists(op.restoreSource.fileId, pinned(ctx))) && (await alreadyRestored(target, ctx))) return
        throw err
      }
      // the file already at the restored content ⇒ that restore landed
      if (!(isVersionChanged(err) && (await alreadyRestored(target, ctx)))) throw err
    }
    return
  }
  // Recorded before the version id was carried: the old two steps.
  if (!op.versionRestored) {
    if (op.restoreSource && !(await filesApi.binItemExists(op.restoreSource.binId, pinned(ctx)))) {
      throw new Error('Cannot undo: the moved file was permanently deleted from the Bin')
    }
    await filesApi.restoreVersion(op.fileId, op.previousVersionId, pinned(ctx))
    op.versionRestored = true  // D134: a retry goes straight to bringing the moved file back
  }
  if (op.restoreSource) {
    await handleDeleteUndo({ id: op.id, timestamp: op.timestamp, type: 'delete', binId: op.restoreSource.binId, fileId: op.restoreSource.fileId, name: op.name }, ctx)
  }
}
registerUndoHandler('addVersion', handleAddVersionUndo)

/** Undo an extract: delete what it created, and restore the version each Replace overwrote. */
export async function handleExtractUndo(op: UndoOperation, ctx?: UndoHandlerContext): Promise<void> {
  if (op.type !== 'extract') return
  // Versions FIRST (as a paste's undo): one that changed since refuses with nothing deleted yet.
  if (!op.versionsRestored) await restoreReplacedVersions(op, ctx)
  op.versionsRestored = true
  // Delete all extracted files, then the created folder — strict (D134): an item already gone is done (counted as not
  // confirmed); any other refusal is thrown, so the undo stays for a retry and the person sees why
  // Each through its job when big (#921: an extract that made a 30,000-file folder used to report success while the
  // inline delete was cut at the edge and kept running); `deletedIds` lets a retry skip what is done.
  for (const fileId of op.extractedFileIds) await deleteCreated(fileId, ctx, op, op.name)
  if (op.createdFolderId) await deleteCreated(op.createdFolderId, ctx, op, op.name)
}
registerUndoHandler('extract', handleExtractUndo)
